import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Mail, Phone, MapPin, Clock, Send, Star, Facebook, Instagram, Linkedin } from 'lucide-react';
import { useNavigate } from "react-router-dom"; // ✅ back to home
import './contact.css';

gsap.registerPlugin(ScrollTrigger);

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    company: '',
    phone: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const infoRef = useRef(null);
  const formRef = useRef(null);
  const orbsRef = useRef([]);

  const navigate = useNavigate(); // ✅ for back to home

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Orbs animation
      orbsRef.current.forEach((orb, index) => {
        if (orb) {
          gsap.to(orb, {
            rotation: 360,
            duration: 15 + index * 3,
            repeat: -1,
            ease: "none"
          });
          
          gsap.to(orb, {
            x: Math.sin(index * 45) * 40,
            y: Math.cos(index * 45) * 25,
            duration: 6 + index * 2,
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut"
          });
        }
      });

      if (headerRef.current) {
        gsap.fromTo(headerRef.current.children, 
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            stagger: 0.2,
            scrollTrigger: { trigger: headerRef.current, start: "top 80%" }
          }
        );
      }

      if (infoRef.current) {
        gsap.fromTo(infoRef.current.children,
          { opacity: 0, x: -50, scale: 0.9 },
          {
            opacity: 1,
            x: 0,
            scale: 1,
            duration: 0.8,
            stagger: 0.15,
            scrollTrigger: { trigger: infoRef.current, start: "top 80%" }
          }
        );
      }

      if (formRef.current) {
        gsap.fromTo(formRef.current,
          { opacity: 0, x: 50 },
          {
            opacity: 1,
            x: 0,
            duration: 1,
            scrollTrigger: { trigger: formRef.current, start: "top 80%" }
          }
        );
      }

      gsap.to(".floating-particles", {
        backgroundPosition: "0% -100%",
        duration: 25,
        repeat: -1,
        ease: "none"
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    gsap.to(e.target.querySelector('.contact-submit'), {
      scale: 0.95,
      duration: 0.1,
      yoyo: true,
      repeat: 1
    });

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitted(true);
    setIsSubmitting(false);

    gsap.fromTo('.success-message', 
      { scale: 0, opacity: 0 },
      { scale: 1, opacity: 1, duration: 0.8, ease: "back.out(1.7)" }
    );

    // ✅ lock scroll on success
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    // ❌ removed auto reset
  };

  if (isSubmitted) {
    return (
      <section ref={sectionRef} className="contact-section">
        <div className="contact-background">
          <div className="gradient-mesh">
            <div ref={el => orbsRef.current[0] = el} className="mesh-gradient mesh-1"></div>
            <div ref={el => orbsRef.current[1] = el} className="mesh-gradient mesh-2"></div>
            <div ref={el => orbsRef.current[2] = el} className="mesh-gradient mesh-3"></div>
          </div>
          <div className="grid-pattern"></div>
          <div className="floating-particles"></div>
          <div className="noise-texture"></div>
        </div>
        
        <div className="contact-container">
          <div className="success-message">
            <div className="success-icon">
              <Star className="star-icon" />
              <div className="success-ripple"></div>
            </div>
            <h2 className="success-title">Message Sent Successfully!</h2>
            <p className="success-subtitle">
              Thank you for reaching out to Beyond The Analysis. We'll get back to you within 24 hours with personalized insights.
            </p>
            <div className="social-links">
              <p>Stay connected with us:</p>
              <div className="social-icons">
                <a href="https://www.facebook.com/profile.php?id=61560885657463" target="_blank" rel="noopener noreferrer">
                  <Facebook />
                </a>
                <a href="https://www.instagram.com/beyondtheanalysis/" target="_blank" rel="noopener noreferrer">
                  <Instagram />
                </a>
                <a href="https://www.linkedin.com/company/beyond-the-analysis/about/?viewAsMember=true" target="_blank" rel="noopener noreferrer">
                  <Linkedin />
                </a>
              </div>
            </div>

            {/* ✅ Back to Home button */}
       
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className="contact-section">
      <div className="contact-background">
        <div className="gradient-mesh">
          <div ref={el => orbsRef.current[0] = el} className="mesh-gradient mesh-1"></div>
          <div ref={el => orbsRef.current[1] = el} className="mesh-gradient mesh-2"></div>
          <div ref={el => orbsRef.current[2] = el} className="mesh-gradient mesh-3"></div>
        </div>
        <div className="grid-pattern"></div>
        <div className="floating-particles"></div>
        <div className="noise-texture"></div>
      </div>

      <div className="contact-container">
        <div ref={headerRef} className="contact-header">
          <div className="achievement-badge">
            <Mail className="badge-icon" />
            <span className="badge-text">Available 24/7</span>
          </div>
          <h1 className="contact-title">
            <span className="animate-word">Get in</span>
            <span className="highlight-word">Touch</span>
          </h1>
          <p className="contact-subtitle">
            Ready to transform your digital presence? Connect with Beyond The Analysis experts for data-driven solutions that deliver real results.
          </p>
        </div>

        <div className="contact-content">
          <div ref={infoRef} className="contact-info">
            {/* info cards */}
            <div className="info-card">
              <div className="info-icon"><Mail /></div>
              <div className="info-content">
                <h3>Email Us</h3>
                <p>marketing@beyondtheanalysis.com</p>
                <p>For all business inquiries</p>
              </div>
            </div>
            {/* <div className="info-card">
              <div className="info-icon"><Phone /></div>
              <div className="info-content">
                <h3>Call Us</h3>
                <p>+91 7570007967</p>
                <p>We are here to help you</p>
              </div>
            </div> */}
            <div className="info-card">
              <div className="info-icon"><MapPin /></div>
              <div className="info-content">
                <h3>Location</h3>
                <p>1309 Coffeen Ave STE 19665, Sheridan, WY, 82801</p>
                  <p>USA</p>
                <p>Serving clients globally</p>
              </div>
            </div>
            <div className="info-card">
              <div className="info-icon"><Clock /></div>
              <div className="info-content">
                <h3>Response Time</h3>
                <p>Within 24 hours</p>
                <p>Mon - Sun: Available</p>
              </div>
            </div>
            <div className="social-card">
              <h3>Follow Us</h3>
              <div className="social-links-grid">
                <a href="https://www.facebook.com/profile.php?id=61560885657463" target="_blank" rel="noopener noreferrer" className="social-link">
                  <Facebook /><span>Facebook</span>
                </a>
                <a href="https://www.instagram.com/beyondtheanalysis/" target="_blank" rel="noopener noreferrer" className="social-link">
                  <Instagram /><span>Instagram</span>
                </a>
                <a href="https://www.linkedin.com/company/beyond-the-analysis/about/?viewAsMember=true" target="_blank" rel="noopener noreferrer" className="social-link">
                  <Linkedin /><span>LinkedIn</span>
                </a>
              </div>
            </div>
          </div>

          <form ref={formRef} className="contact-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name" className="form-label">Full Name</label>
              <input type="text" id="name" name="name" value={formData.name}
                onChange={handleInputChange} className="form-input" required placeholder="Enter your full name" />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email" className="form-label">Email Address</label>
                <input type="email" id="email" name="email" value={formData.email}
                  onChange={handleInputChange} className="form-input" required placeholder="your.email@company.com" />
              </div>
              <div className="form-group">
                <label htmlFor="phone" className="form-label">Phone Number</label>
                <input type="tel" id="phone" name="phone" value={formData.phone}
                  onChange={handleInputChange} className="form-input" placeholder="+91 7570007967" />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="company" className="form-label">Company (Optional)</label>
                <input type="text" id="company" name="company" value={formData.company}
                  onChange={handleInputChange} className="form-input" placeholder="Your company name" />
              </div>
              <div className="form-group">
                <label htmlFor="subject" className="form-label">Subject</label>
                <input type="text" id="subject" name="subject" value={formData.subject}
                  onChange={handleInputChange} className="form-input" required placeholder="What's this about?" />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="message" className="form-label">Message</label>
              <textarea id="message" name="message" value={formData.message}
                onChange={handleInputChange} className="form-textarea" rows="6" required
                placeholder="Tell us about your project, challenges, or how we can help you achieve your digital marketing goals..."></textarea>
            </div>

            <button type="submit" className="cta-button primary large contact-submit" disabled={isSubmitting}>
              <Send />
              {isSubmitting ? 'Sending...' : 'Send Message'}
              <div className="button-glow"></div>
            </button>

            <p className="form-note">
              We respect your privacy. Your information will be used solely to respond to your inquiry.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
